/**
 * ComparisonFieldRow Component
 *
 * Single field comparison row with difference highlighting.
 * Shows anchor value, comparison value, and delta for numeric fields.
 */

import React from 'react'
import { Typography, Tooltip, Tag } from 'antd'
import { ArrowRightOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import type { FieldMetadata } from '@/services/metadataService'
import type { FieldDifference } from './types'
import {
  formatCellValue,
  isLifecycleField,
  renderLifecycleStatus,
} from '../../utils/columnFormatters'
import { isJsonField, isJsonValue } from '../../utils/jsonFormatters'
import { JsonValueDisplay } from '../JsonValueDisplay'

const { Text } = Typography

/**
 * Format numeric delta for display.
 * Includes + prefix for positive, - for negative.
 */
function formatNumericDelta(delta: number, metadata: FieldMetadata): string {
  const prefix = delta > 0 ? '+' : ''
  const pyType = (metadata.py_type || 'string').toLowerCase()

  // Format based on type
  if (pyType === 'integer' || pyType === 'int') {
    return `${prefix}${Math.round(delta)}`
  }

  // Default to 2 decimal places for numeric types
  return `${prefix}${delta.toFixed(2)}`
}

/**
 * Format percentage delta for display.
 */
function formatPercentDelta(percent: number): string {
  const prefix = percent > 0 ? '+' : ''
  return `${prefix}${percent.toFixed(1)}%`
}

/**
 * Get the field title from metadata.
 */
function getFieldTitle(fieldKey: string, metadata?: FieldMetadata): string {
  if (metadata?.title) {
    return metadata.title
  }

  // Convert camelCase to Title Case
  return fieldKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

/**
 * Render a field value with appropriate formatting.
 */
function renderFieldValue(
  value: unknown,
  fieldKey: string,
  metadata?: FieldMetadata
): React.ReactNode {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <Text type="secondary" italic>-</Text>
  }

  // Handle lifecycle state fields specially
  if (metadata && isLifecycleField(fieldKey, metadata) && typeof value === 'string') {
    return renderLifecycleStatus(value)
  }

  // JSON fields - use interactive popover display
  if ((metadata && isJsonField(metadata)) || isJsonValue(value)) {
    return <JsonValueDisplay value={value} maxHeight={200} collapsed={1} />
  }

  // Use column formatter for other fields
  if (metadata) {
    const formatted = formatCellValue(value, metadata)
    return <Text>{formatted}</Text>
  }

  return <Text>{String(value)}</Text>
}

export interface ComparisonFieldRowProps {
  /** Field difference data */
  difference: FieldDifference
  /** Field metadata for formatting */
  metadata?: FieldMetadata
  /** Whether this is a key field (business-critical) */
  isKeyField?: boolean
  /** Compact mode for smaller displays */
  compact?: boolean
  /** Whether a comparison record is selected (false = show "--" placeholder) */
  hasComparison?: boolean
}

/**
 * Single field comparison row.
 *
 * @example
 * ```tsx
 * <ComparisonFieldRow
 *   difference={{
 *     fieldKey: 'price',
 *     anchorValue: 45.00,
 *     comparisonValue: 42.00,
 *     isDifferent: true,
 *     numericDelta: 3.00,
 *     percentDelta: 7.14,
 *   }}
 *   metadata={fields.price}
 *   isKeyField={true}
 * />
 * ```
 */
export function ComparisonFieldRow({
  difference,
  metadata,
  isKeyField = false,
  compact = false,
  hasComparison = true,
}: ComparisonFieldRowProps): JSX.Element {
  const {
    fieldKey,
    anchorValue,
    comparisonValue,
    isDifferent,
    numericDelta,
    percentDelta,
  } = difference

  const title = getFieldTitle(fieldKey, metadata)
  const hasNumericDelta = numericDelta !== undefined && numericDelta !== 0

  // Determine delta direction for styling
  const deltaDirection = numericDelta
    ? numericDelta > 0
      ? 'increase'
      : 'decrease'
    : null

  // Build accessible label for screen readers
  const accessibleLabel = !hasComparison
    ? `${title}${isKeyField ? ' (key field)' : ''}: ${String(anchorValue ?? 'empty')}, no comparison record selected`
    : isDifferent
      ? `${title}${isKeyField ? ' (key field)' : ''}: changed from ${String(comparisonValue ?? 'empty')} to ${String(anchorValue ?? 'empty')}${hasNumericDelta && metadata ? `, delta ${formatNumericDelta(numericDelta!, metadata)}` : ''}`
      : `${title}: ${String(anchorValue ?? 'empty')}, unchanged`

  return (
    <div
      role="listitem"
      aria-label={accessibleLabel}
      className={`comparison-field-row ${isDifferent ? 'is-different' : ''} ${isKeyField ? 'is-key-field' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: compact ? '6px 12px' : '10px 16px',
        backgroundColor: isDifferent ? 'var(--rp-temporal-orange-soft)' : undefined,
        borderBottom: '1px solid var(--rp-border-subtle)',
        borderLeft: isKeyField ? '3px solid var(--rp-blue-primary)' : undefined,
        transition: 'background-color 0.2s',
      }}
    >
      {/* Field Label */}
      <div
        style={{
          width: compact ? '30%' : '25%',
          minWidth: 100,
          paddingRight: 12,
        }}
      >
        <Tooltip title={metadata?.description || undefined}>
          <Text
            strong={isKeyField}
            style={{
              fontSize: compact ? 12 : 13,
              color: isKeyField ? 'var(--rp-blue-primary)' : undefined,
            }}
          >
            {title}
            {isKeyField && (
              <Tag
                color="blue"
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  padding: '0 4px',
                  lineHeight: '16px',
                }}
              >
                Key
              </Tag>
            )}
          </Text>
        </Tooltip>
      </div>

      {/* Comparison Value (Left - current/baseline) */}
      <div
        className={`comparison-value-cell ${!hasComparison ? 'no-comparison' : ''}`}
        style={{
          flex: 1,
          paddingRight: 12,
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: '4px 8px',
            backgroundColor: 'var(--rp-surface-tertiary)',
            borderRadius: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {hasComparison ? (
            renderFieldValue(comparisonValue, fieldKey, metadata)
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>--</Text>
          )}
        </div>
      </div>

      {/* Arrow indicator */}
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          color: isDifferent ? 'var(--rp-temporal-orange)' : 'var(--rp-border-subtle)',
        }}
      >
        <ArrowRightOutlined style={{ fontSize: compact ? 12 : 14 }} />
      </div>

      {/* Anchor Value (Right - current/new) */}
      <div
        style={{
          flex: 1,
          paddingRight: hasNumericDelta ? 12 : 0,
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: '4px 8px',
            backgroundColor: isDifferent ? 'var(--rp-temporal-orange-soft)' : 'var(--rp-surface-tertiary)',
            borderRadius: 4,
            border: isDifferent ? '1px solid var(--rp-temporal-orange-border)' : undefined,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {renderFieldValue(anchorValue, fieldKey, metadata)}
        </div>
      </div>

      {/* Numeric Delta */}
      {hasNumericDelta && metadata && (
        <div
          style={{
            minWidth: compact ? 80 : 100,
            textAlign: 'right',
          }}
        >
          <Tooltip
            title={
              percentDelta !== undefined
                ? `${formatPercentDelta(percentDelta)} change`
                : undefined
            }
          >
            <Tag
              color={deltaDirection === 'increase' ? 'orange' : 'green'}
              icon={
                deltaDirection === 'increase' ? (
                  <ArrowUpOutlined />
                ) : (
                  <ArrowDownOutlined />
                )
              }
              style={{
                fontSize: compact ? 11 : 12,
                marginRight: 0,
              }}
            >
              {formatNumericDelta(numericDelta!, metadata)}
              {percentDelta !== undefined && (
                <span style={{ marginLeft: 4, opacity: 0.8 }}>
                  ({formatPercentDelta(percentDelta)})
                </span>
              )}
            </Tag>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export default ComparisonFieldRow
